import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SharedModal from 'src/ui/modal/shared-modal';
import { FOLDER_DIRECTORIES } from '../../../constants/folder';
import ExpandMoreImg from '../../../images/expand_more.svg';
import translation, { world } from '../../../languages';
import { getFolders } from '../../../reducers/folders';
import foldersService from '../../../services/folders/folders-service';
import qualityEvents from '../../../services/quality-events/quality-events';
import { OverlayTrigger } from '../../../ui/overlay-trigger/overlay-trigger';
import Tooltip from '../../../ui/tooltip';
import cn from '../../../utils/classnames';
import schoolAccount from '../../../utils/school-account';
import Blinder from '../../blinder/blinder';
import Input from '../../input/input';
import RadioButton from '../../radio-button/radio-button';
import useFoldersSplit from '../../../hooks/use-folders-split';
import { calculateMaxDepth, generateBranch } from './folder-wizard.helpers';
import './folder-wizard.scss';

type FolderWizardProps = TranslationProps & {
    directory: FOLDER_DIRECTORIES;
    onClose: () => void;
    onSuccessCallback?: () => void;
    editedFolder?: Folder;
    onCreate?: VoidFunction;
};

const FolderWizard: FC<FolderWizardProps> = ({ onClose, directory, onSuccessCallback, editedFolder, onCreate, t }) => {
    const dispatch = useDispatch();
    const isEdit = Boolean(editedFolder);
    const user = useSelector((state: RootState) => state.main.user);
    const isSchoolTeacher = Boolean(schoolAccount.hasSchool(user)) && !schoolAccount.isAdmin(user);
    const { ownTree, ownList, tree, list } = useFoldersSplit(directory);
    const foldersTree = isSchoolTeacher ? ownTree : tree;
    const foldersList = isSchoolTeacher ? ownList : list;
    const [pending, setPending] = useState(false);
    const [showFolderSelect, setShowFolderSelect] = useState(false);
    const [title, setTitle] = useState('');
    const [titleError, setTitleError] = useState(false);
    const [selectedParentFolderId, setSelectedParentFolderId] = useState<null | number>(null);
    const [maxNestDepthAllowed, setMaxNestDepthAllowed] = useState(3);
    const modalBtnConfig = [
        {
            action: () => onClose(),
            className: 'styled secondary',
            text: t('search.folders.folderWizard.cancelBtn')
        },
        {
            action: () => (isEdit ? update() : create()),
            className: `styled yellow ${pending ? 'disabled' : ''}`,
            text: isEdit ? t('search.folders.folderWizard.continue') : t('search.folders.folderWizard.createBtn')
        }
    ];

    useEffect(() => {
        if (isEdit) {
            setTitle(editedFolder!.title);
            // @ts-ignore
            setSelectedParentFolderId(editedFolder.parent_id);
        }
    }, []);

    useEffect(() => {
        if (isEdit && foldersList.length) {
            const branch = generateBranch(editedFolder!.id, foldersList as Folder[]);

            if (branch) {
                setMaxNestDepthAllowed(3 - calculateMaxDepth(branch));
            }
        }
    }, [foldersList.length]);

    const create = () => {
        const isFormValid = validateForm();

        if (!isFormValid || pending) {
            return;
        }

        setPending(true);
        foldersService
            .create({
                parent_id: selectedParentFolderId,
                title,
                type: directory,
                world
            })
            .then(() => {
                onCreate?.();
                processResponseSuccess();
            });
    };

    const update = () => {
        const isFormValid = validateForm();
        let { parent_id } = editedFolder!;

        if (!isFormValid || pending) {
            return;
        }

        if (showFolderSelect) {
            parent_id = selectedParentFolderId;
        }

        setPending(true);
        foldersService
            .update(
                {
                    parent_id,
                    title
                },
                {
                    directory,
                    id: editedFolder!.id
                }
            )
            .then(processResponseSuccess);
    };

    const validateForm = () => {
        if (!title) {
            setTitleError(true);

            return false;
        }

        return true;
    };

    const processResponseSuccess = () => {
        try {
            qualityEvents.myMaterialsPage.folderCreateUpdate(isEdit);
        } catch (e) {
            console.error(e);
        }

        setPending(false);
        dispatch(getFolders(directory));
        onSuccessCallback && onSuccessCallback();
        onClose();
    };

    const isForbiddenForEditing = (folder: Folder) => {
        return folder.children.some((item) => editedFolder!.id === Number(item.id)) || folder.id === editedFolder!.id;
    };

    const radioButton = (folder: Folder, disabled: boolean) => {
        return (
            <RadioButton
                id={`folder-${folder.id}`}
                onChange={() => setSelectedParentFolderId(folder.id)}
                label={folder.title}
                disabled={disabled}
                checked={selectedParentFolderId === folder.id}
            />
        );
    };

    const renderStructure = (tree: Folder[], depth: number) => {
        return (
            <>
                {tree.map((folder: Folder) => {
                    const disabled = depth >= 3;
                    const nextLevel = depth + 1;

                    if (isEdit && (isForbiddenForEditing(folder) || depth >= maxNestDepthAllowed)) {
                        return null;
                    }

                    return (
                        <div
                            className={cn('folder relative', {
                                subfolder: folder.parent_id
                            })}
                        >
                            <div className={cn('mt-2 mb-2 position-relative', { disabled })}>
                                {disabled ? (
                                    <OverlayTrigger
                                        rootClose
                                        overlay={
                                            <Tooltip.Simple id='folder-nesting-level-limit'>
                                                {t('search.folders.folderWizard.disabledRadioButtonTooltip')}
                                            </Tooltip.Simple>
                                        }
                                        placement='top'
                                        trigger='hover'
                                    >
                                        <span>{radioButton(folder, true)}</span>
                                    </OverlayTrigger>
                                ) : (
                                    <span>{radioButton(folder, false)}</span>
                                )}
                            </div>
                            <div className='children'>{renderStructure(folder.children, nextLevel)}</div>
                        </div>
                    );
                })}
            </>
        );
    };

    const toggleFolderSelect = () => {
        setShowFolderSelect(!showFolderSelect);
        setSelectedParentFolderId(null);
    };

    const modalBody = () => {
        return (
            <div className='mb-2 position-relative'>
                {pending && <Blinder />}
                {!isEdit && <p>{t('search.folders.folderWizard.creationSubtitle')}</p>}
                <div>
                    <div>
                        <span className='tsc-label'>{t('search.folders.folderWizard.inputLabel')}</span>
                    </div>
                    <Input defaultValue={title} error={titleError} onChange={(e) => setTitle(e.target.value)} />
                </div>
                {Boolean(foldersTree.length && maxNestDepthAllowed > 0) && (
                    <div className='mt-3'>
                        <div>
                            <span className='tsc-label'>{t('search.folders.folderWizard.dropdownLabel')}</span>
                        </div>
                        <div>
                            <div
                                className={cn(
                                    'subfolder-select cursor-pointer d-flex align-items-center justify-content-between',
                                    {
                                        expanded: showFolderSelect
                                    }
                                )}
                                onClick={toggleFolderSelect}
                            >
                                {t('search.folders.folderWizard.dropdownText')}
                                <img className='caret-img' src={ExpandMoreImg} height={8} alt='' />
                            </div>
                        </div>
                        {Boolean(showFolderSelect) && (
                            <div className='folders-tree mt-1'>
                                <div className='folder'>
                                    <RadioButton
                                        id='set-root-level'
                                        onChange={() => setSelectedParentFolderId(null)}
                                        label={t('search.folders.folderWizard.setAsRoot')}
                                        checked={!selectedParentFolderId}
                                    />
                                </div>
                                {renderStructure(foldersTree, 0)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <SharedModal
                dialogClassName='create-folder-modal'
                containerClassName='create-folder-modal-container'
                onClose={onClose}
                closeOnOutsideClick
                body={modalBody()}
                btnConfig={modalBtnConfig}
                title={
                    <span className='tsc-h4'>
                        {isEdit
                            ? t('search.folders.folderWizard.editingTitle')
                            : t('search.folders.folderWizard.creationTitle')}
                    </span>
                }
            />
        </>
    );
};

const translate = translation('search');

export default translate(FolderWizard);
